'use client'

import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdKeyboardArrowDown } from 'react-icons/md'

export interface ActionMenuItem {
    /** Identificador estável — usado como key e para o divisor não quebrar a ordem. */
    key: string
    label: string
    icon: ReactNode
    onClick: () => void
    /** Ação destrutiva (cancelar, excluir): vai para o fim, em vermelho, após divisor. */
    danger?: boolean
    disabled?: boolean
    loading?: boolean
    /** Texto do title (tooltip) — útil para explicar por que está desabilitada. */
    title?: string
}

interface ActionsMenuProps {
    items: ActionMenuItem[]
    /** Texto do gatilho. Fica VISÍVEL no botão — não é só rótulo acessível. */
    label?: string
    disabled?: boolean
    /**
     * Gatilho customizado, para quando o próprio conteúdo é o alvo do clique —
     * o nome do cliente no cabeçalho do chat, por exemplo. Substitui o botão
     * "Ações"; use apenas quando o elemento já comunica que é clicável, senão
     * vale o gatilho padrão com rótulo.
     */
    trigger?: ReactNode
    /** Lado em que o menu abre. Default: direita. */
    align?: 'left' | 'right'
    /**
     * Renderiza o menu num portal, com posição fixa, em vez de `absolute` dentro do
     * gatilho.
     *
     * Necessário quando algum ancestral tem `overflow-hidden` — o caso dos cards das
     * listas do prontuário, que arredondam o conteúdo assim. Contra overflow o
     * `z-index` não vale nada: o menu era simplesmente cortado pela borda do card.
     */
    portal?: boolean
}

/**
 * Menu de ações secundárias das telas de detalhe (exame, consulta, procedimento).
 *
 * O cabeçalho tinha até cinco botões lado a lado — PDF, imprimir, e-mail, editar,
 * cancelar, excluir — e os dois destrutivos eram ícones sem rótulo, vizinhos do
 * editar. Recolher tudo aqui resolve as duas coisas: o cabeçalho volta a ter uma
 * ação em evidência (a primária de cada tela fica FORA deste menu) e as
 * destrutivas ganham rótulo, cor e um divisor que as separa do resto.
 *
 * O gatilho é um botão com TEXTO ("Ações" + chevron), nunca um kebab de três pontos:
 * escondido atrás de um ícone, o menu vira adivinhação para quem opera a tela.
 *
 * Não usa `<details>`/`tabindex` do DaisyUI: com foco preso, o menu ficava aberto
 * depois do clique enquanto o modal da ação abria por baixo.
 */
export default function ActionsMenu({
    items,
    label = 'Ações',
    disabled = false,
    trigger,
    align = 'right',
    portal = false,
}: ActionsMenuProps) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    // No modo portal o menu fica fora de `ref`: sem olhar para ele também, o
    // mousedown no item contava como "clique fora" e fechava o menu antes do click.
    const menuRef = useRef<HTMLUListElement>(null)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

    // Posição do menu no modo portal: medida a partir do gatilho, e refeita a cada
    // abertura. `useLayoutEffect` para não haver um quadro com o menu no lugar errado.
    useLayoutEffect(() => {
        if (!portal || !open || !ref.current) return

        const measure = () => {
            const rect = ref.current?.getBoundingClientRect()
            if (!rect) return
            const MENU_WIDTH = 224 // w-56
            const left =
                align === 'left'
                    ? rect.left
                    : Math.max(8, rect.right - MENU_WIDTH)
            setPosition({ top: rect.bottom + 4, left })
        }

        measure()
        // Rolar com o menu aberto o deixaria flutuando longe do botão.
        window.addEventListener('scroll', measure, true)
        window.addEventListener('resize', measure)
        return () => {
            window.removeEventListener('scroll', measure, true)
            window.removeEventListener('resize', measure)
        }
    }, [portal, open, align])

    // Clicar fora e Esc fecham — um menu que só fecha ao escolher uma ação prende
    // o usuário que abriu por engano.
    useEffect(() => {
        if (!open) return

        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node
            if (ref.current?.contains(target) || menuRef.current?.contains(target)) return
            setOpen(false)
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    const visible = items.filter(Boolean)
    if (visible.length === 0) return null

    const normal = visible.filter(i => !i.danger)
    const dangerous = visible.filter(i => i.danger)

    const renderItem = (item: ActionMenuItem) => (
        <li key={item.key}>
            <button
                type="button"
                className={`flex items-center gap-3 ${item.danger ? 'text-error' : ''}`}
                disabled={item.disabled || item.loading}
                title={item.title}
                onClick={() => {
                    setOpen(false)
                    item.onClick()
                }}
            >
                {item.loading ? (
                    <span className="loading loading-spinner loading-xs" />
                ) : (
                    <span className="text-lg">{item.icon}</span>
                )}
                {item.label}
            </button>
        </li>
    )

    const menuContent = (
        <>
            {normal.map(renderItem)}
            {normal.length > 0 && dangerous.length > 0 && (
                <li className="menu-title px-0 py-1">
                    <div className="border-t border-base-300/60" />
                </li>
            )}
            {dangerous.map(renderItem)}
        </>
    )

    return (
        <div className="relative" ref={ref}>
            {/* Rótulo por extenso, não um kebab: um ícone solto de três pontos esconde as
                ações atrás de uma adivinhação. Quem opera a tela todo dia precisa VER que
                há mais ações ali — e o chevron diz que abre. */}
            {trigger ? (
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    disabled={disabled}
                    className="text-left w-full disabled:opacity-100"
                    onClick={() => setOpen(o => !o)}
                >
                    {trigger}
                </button>
            ) : (
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    disabled={disabled}
                    className="btn btn-outline btn-sm gap-2"
                    onClick={() => setOpen(o => !o)}
                >
                    {label}
                    <MdKeyboardArrowDown
                        className={`text-lg transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                </button>
            )}

            {open && !portal && (
                <ul
                    role="menu"
                    className={`menu menu-sm absolute ${align === 'left' ? 'left-0' : 'right-0'} top-full mt-1 z-50 w-56 rounded-xl bg-base-100 p-2 shadow-lg border border-base-300/50`}
                >
                    {menuContent}
                </ul>
            )}

            {open &&
                portal &&
                position &&
                createPortal(
                    <ul
                        ref={menuRef}
                        role="menu"
                        // `z-[9999]`: o menu sai para o topo do documento e passa a
                        // disputar com modais e overlays, que usam z alto.
                        className="menu menu-sm fixed z-[9999] w-56 rounded-xl bg-base-100 p-2 shadow-lg border border-base-300/50"
                        style={{ top: position.top, left: position.left }}
                        // O clique aqui não pode borbulhar para o card que abriu o
                        // menu — nas listas do prontuário ele navega para o detalhe.
                        onClick={event => event.stopPropagation()}
                    >
                        {menuContent}
                    </ul>,
                    document.body
                )}
        </div>
    )
}
